import { useState } from "react";
import { Tooltip } from "react-tooltip";
import {
	DataTable,
	DataTableExpandedRows,
	DataTableRowEvent,
	DataTableValueArray,
} from "primereact/datatable";
import { Column } from "primereact/column";
import {
	CheckBadgeIcon as CheckBadgeIconOutline,
	ClockIcon,
	ExclamationTriangleIcon,
	TrashIcon,
} from "@heroicons/react/24/outline";
import { CheckBadgeIcon } from "@heroicons/react/24/solid";
import { CustomSortIcon } from "../../../components/DatatableHelpers";
import FormattedDateAndTime from "../../../components/FormattedDateAndTime";
import { DraftStatus } from "../writingApiSlice";
import { UserDraftHistoryEssay } from "./draftAutosaveHistoryApiSlice";
import AutosaveHistoryDraftTable from "./AutosaveHistoryDraftTable";

interface EssayTableProps {
	essays: UserDraftHistoryEssay[];
	selectedPrompt?: string;
	selectedStatus?: string;
}

export default function AutosaveHistoryEssayTable({
	essays,
	selectedPrompt,
	selectedStatus,
}: EssayTableProps) {
	const [expandedRows, setExpandedRows] = useState<DataTableExpandedRows | DataTableValueArray>();

	// Table
	const promptNameBodyTemplate = (row: UserDraftHistoryEssay) => {
		return (
			<div className="line-clamp-2 hover:line-clamp-none">
				{row.promptTitle.replace(/\//g, "/\u200b")}
			</div>
		);
	};

	const dateBodyTemplate = (row: UserDraftHistoryEssay) => {
		return <FormattedDateAndTime date={row.lastUpdateDate} />;
	};

	const draftCountBodyTemplate = (row: UserDraftHistoryEssay) => {
		return row.drafts.length;
	};

	const draftCountSortFunction = (event: any) => {
		const data = [...event.data];
		data.sort((data1, data2) => {
			return (data1.drafts.length - data2.drafts.length) * event.order;
		});
		return data;
	};

	const statusBodyTemplate = (row: UserDraftHistoryEssay) => {
		return (
			<div className="flex items-center">
				{row.lastDraftStatus === DraftStatus.Finalized ? (
					<CheckBadgeIcon
						className="size-5 text-green-700"
						aria-hidden="true"
						data-tooltip-id="completed-tooltip"
					/>
				) : row.lastDraftStatus === DraftStatus.Scored ? (
					<CheckBadgeIconOutline
						className="size-5 text-green-700"
						aria-hidden="true"
						data-tooltip-id="completed-tooltip"
					/>
				) : row.lastDraftStatus === DraftStatus.ScoringFailed ||
				  row.lastDraftStatus === DraftStatus.Incomplete ? (
					<ClockIcon
						className="size-5 text-yellow-500"
						aria-hidden="true"
						data-tooltip-id="in-progress-tooltip"
					/>
				) : row.lastDraftStatus === DraftStatus.Deleted ? (
					<TrashIcon
						className="size-5 text-red-500"
						aria-hidden="true"
						data-tooltip-id="deleted-tooltip"
					/>
				) : (
					<ExclamationTriangleIcon
						className="size-5 text-red-500"
						aria-hidden="true"
						data-tooltip-id="not-started-tooltip"
					/>
				)}
				<span className="ml-2">{row.lastDraftStatusDescription}</span>
				<Tooltip id="completed-tooltip" place="top" content="Completed" />
				<Tooltip id="in-progress-tooltip" place="top" content="In Progress" />
				<Tooltip id="not-started-tooltip" place="top" content="Not Started" />
				<Tooltip id="deleted-tooltip" place="top" content="Deleted" />
			</div>
		);
	};

	const rowExpansionTemplate = (row: UserDraftHistoryEssay) => {
		return <AutosaveHistoryDraftTable drafts={row.drafts} promptTitle={row.promptTitle} />;
	};

	const onRowExpand = (event: any) => {
		// clicking on the row expander triggers a nested DataTableRowEvent
		const target =
			event.originalEvent.currentTarget ??
			(event.originalEvent.originalEvent.currentTarget as HTMLElement);

		if (target) {
			const table = target.closest("table");
			table &&
				[...table.getElementsByClassName("expanded")].forEach((e: HTMLElement) =>
					e.classList.remove("expanded")
				);

			const tr = target.closest("tr");
			tr && tr?.classList.add("expanded");
		}
		setExpandedRows({ [event.data.id]: true });
	};

	const onRowCollapse = (event: any) => {
		// clicking on the row expander triggers a nested DataTableRowEvent
		const target =
			event.originalEvent.currentTarget ??
			(event.originalEvent.originalEvent.currentTarget as HTMLElement);

		if (target) {
			const table = target.closest("table");
			table &&
				[...table.getElementsByClassName("expanded")].forEach((e: HTMLElement) =>
					e.classList.remove("expanded")
				);
		}
		setExpandedRows({});
	};

	const onRowClick = (event: DataTableRowEvent) => {
		const xRows: any = expandedRows;

		if (xRows && xRows[event.data.id]) {
			onRowCollapse(event);
		} else {
			onRowExpand(event);
		}
	};

	// Filter drafts by selected prompt and status
	const filteredEssays = essays.filter((essay) => {
		const matchesPrompt =
			!selectedPrompt || selectedPrompt === "All" || essay.promptTitle === selectedPrompt;
		const matchesStatus =
			!selectedStatus ||
			selectedStatus === "All" ||
			essay.lastDraftStatusDescription === selectedStatus;
		return matchesPrompt && matchesStatus;
	});

	return (
		<>
			<DataTable
				value={filteredEssays}
				dataKey="id"
				stripedRows
				sortIcon={CustomSortIcon}
				emptyMessage="No results found"
				className="tbl tbl-data tbl-datatable pt-4"
				selectionMode="single"
				expandedRows={expandedRows}
				onRowClick={onRowClick}
				onRowExpand={onRowExpand}
				onRowCollapse={onRowCollapse}
				rowExpansionTemplate={rowExpansionTemplate}
				rowClassName={() => "group cursor-pointer"}>
				<Column
					expander={true}
					headerClassName="!pl-3 !pr-0.5 w-8"
					className="!pl-3 !pr-0.5 w-8"></Column>
				<Column
					header="Prompt"
					body={promptNameBodyTemplate}
					sortable
					sortField="promptTitle"
				/>
				<Column
					header="Last Updated"
					body={dateBodyTemplate}
					sortable
					sortField="lastUpdateDate"
				/>
				<Column
					header="Last Draft Status"
					body={statusBodyTemplate}
					sortable
					sortField="lastDraftStatusDescription"
				/>
				<Column
					header="Draft Count"
					body={draftCountBodyTemplate}
					sortField="drafts"
					sortable
					sortFunction={draftCountSortFunction}
				/>
			</DataTable>
		</>
	);
}
